/**
 * 1.7.9 — the month, three ways (D-1.7.9-6), from Events → Month and — for that one event — from an event's page:
 *
 *   Print            a large-print page on ABF letterhead (18 pt bold, English or Spanish chosen on the sheet), made
 *                    for handing to a tenant;
 *   Copy as text     the same list as plain text on the clipboard — one event per line, a blank line between — with
 *                    a "Copied" a screen reader hears;
 *   Email to a person  find a tenant by name or apartment; one press queues the list to their own email on file, in
 *                    their language, through the Outbox and its cancel window. With no email on file the button says
 *                    so and offers Print instead.
 *
 * The three are made from the same lines (`shared/event-month.ts`). Nothing here sends by itself.
 */
import { useRef, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SERVICE_TZ } from '@shared/dates';
import { monthText, type MonthEntry, type MonthLang, type MonthPerson, type MonthSent } from '@shared/event-month';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { errorText } from '@/features/people/api';
import { localeFor } from '@/lib/dates';
import { saveBase64 } from '../page175/save';
import { emailMonthReq, printLargeReq, useMonthPeople, useMonthSent } from './api';

type Say = (text: string, bad?: boolean) => void;

export interface MonthShareProps {
  month: string;
  /** What is shared, in words: "October 2026", or the event's name. */
  what: string;
  /** The lines Copy as text copies (the month's events that are not cancelled, or the one event). */
  entries: readonly MonthEntry[];
  /** Set on an event's page: that one event only. */
  eventId?: string | null;
  say: Say;
}

const IDS = {
  month: { group: 'month-share', print: 'month-print', copy: 'month-copy-text', email: 'month-email', lang: 'month-print-lang', go: 'month-print-go', sheet: 'month-print-sheet' },
  one: { group: 'ev-share', print: 'ev-share-print', copy: 'ev-share-copy', email: 'ev-share-email', lang: 'ev-share-print-lang', go: 'ev-share-print-go', sheet: 'ev-share-print-sheet' },
} as const;

/** The three presses. On the Month view they sit among the month's actions; on an event's page they are a small row. */
export function MonthShare({ month, what, entries, eventId = null, say }: MonthShareProps) {
  const { t, i18n } = useTranslation();
  const lang: MonthLang = i18n.language === 'es' ? 'es' : 'en';
  const qc = useQueryClient();
  const one = !!eventId;
  const ids = one ? IDS.one : IDS.month;
  const [printOpen, setPrintOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [printLang, setPrintLang] = useState<MonthLang>(lang);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const copy = async () => {
    const text = monthText(entries, lang);
    if (!text) return say(t('ev179m.share.copiedNone'), true);
    try {
      await navigator.clipboard.writeText(text);
      say(t('ev179m.share.copied', { count: entries.length }));
    } catch {
      say(t('ev179m.share.copyFailed'), true);
    }
  };

  const print = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await printLargeReq(month, printLang, eventId);
      saveBase64(r.name, r.base64, 'application/pdf');
      setPrintOpen(false);
      say(t('ev179m.share.printed', { count: r.events, name: r.name }));
    } catch (x) {
      const m = errorText(x);
      setErr(m);
      say(m, true);
    } finally {
      setBusy(false);
    }
  };

  const open = (which: 'print' | 'email', from: HTMLElement) => {
    opener.current = from;
    setErr(null);
    if (which === 'print') setPrintOpen(true);
    else setEmailOpen(true);
  };

  return (
    <>
      <div className={one ? 'ev179m-share is-one' : 'ev179m-share'} role="group" aria-label={one ? t('ev179m.share.groupOne') : t('ev179m.share.group')} data-testid={ids.group}>
        {one ? <span className="ev179m-share-label">{t('ev179m.share.one')}</span> : null}
        <Button variant="secondary" size={one ? 'sm' : 'default'} onClick={(x) => open('print', x.currentTarget)} data-testid={ids.print}>
          <Icon name="printer" /> {one ? t('ev179m.share.printOne') : t('ev179m.share.print')}
        </Button>
        <Button variant="secondary" size={one ? 'sm' : 'default'} onClick={() => copy()} data-testid={ids.copy}>
          <Icon name="copy" /> {one ? t('ev179m.share.copyOne') : t('ev179m.share.copy')}
        </Button>
        <Button variant="secondary" size={one ? 'sm' : 'default'} onClick={(x) => open('email', x.currentTarget)} data-testid={ids.email}>
          <Icon name="mail" /> {one ? t('ev179m.share.emailOne') : t('ev179m.share.email')}
        </Button>
      </div>

      <Sheet open={printOpen} onOpenChange={(o) => (!o && !busy ? setPrintOpen(false) : undefined)}>
        {printOpen ? (
          <SheetContent
            title={t('ev179m.share.printTitle', { what })}
            returnFocusTo={opener}
            data-testid={ids.sheet}
            footer={
              <>
                <Button variant="secondary" disabled={busy} onClick={() => setPrintOpen(false)} data-testid={`${ids.sheet}-cancel`}>
                  {t('ev179m.share.cancel')}
                </Button>
                <Button disabled={busy} aria-busy={busy || undefined} onClick={() => print()} data-testid={ids.go}>
                  <Icon name="printer" /> {busy ? t('ev179m.share.printing') : t('ev179m.share.printGo')}
                </Button>
              </>
            }
          >
            <div className="ev179m-stack">
              <p className="help">{t('ev179m.share.printLead')}</p>
              <div className="field">
                <label htmlFor={ids.lang}>{t('ev179m.share.lang')}</label>
                <select id={ids.lang} className="input" value={printLang} onChange={(x) => setPrintLang(x.target.value === 'es' ? 'es' : 'en')} data-testid={ids.lang}>
                  <option value="en">{t('ev179m.share.en')}</option>
                  <option value="es">{t('ev179m.share.es')}</option>
                </select>
              </div>
              {err ? (
                <p className="field-err" role="alert" data-testid={`${ids.sheet}-err`}>
                  {err}
                </p>
              ) : null}
            </div>
          </SheetContent>
        ) : null}
      </Sheet>

      <Sheet open={emailOpen} onOpenChange={(o) => (!o ? setEmailOpen(false) : undefined)}>
        {emailOpen ? (
          <EmailMonthSheet
            month={month}
            what={what}
            eventId={eventId}
            returnFocusTo={opener}
            onClose={() => setEmailOpen(false)}
            onQueued={(message) => {
              setEmailOpen(false);
              say(message);
              void qc.invalidateQueries({ queryKey: ['events-month-sent', month] });
            }}
            onPrintInstead={(personLang) => {
              // the sheet they came from is gone: the page for this person is in their language
              setEmailOpen(false);
              setPrintLang(personLang);
              setErr(null);
              setPrintOpen(true);
            }}
          />
        ) : null}
      </Sheet>
    </>
  );
}

/**
 * Find the tenant, see whether the list can be emailed to them, and press once. 1.8.2: also opened from Events → Print
 * (`upcoming`: all upcoming events rather than the month).
 */
export function EmailMonthSheet({ month, what, eventId, upcoming = false, returnFocusTo, onClose, onQueued, onPrintInstead }: { month: string; what: string; eventId: string | null; upcoming?: boolean; returnFocusTo: RefObject<HTMLElement | null>; onClose: () => void; onQueued: (message: string) => void; onPrintInstead: (lang: MonthLang) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<MonthPerson | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const people = useMonthPeople(month, picked ? '' : q);
  const hits = people.data?.people ?? [];
  const can = picked?.canEmail === 'yes';
  const hitWords = (p: MonthPerson) => (p.canEmail === 'yes' ? t('ev179m.share.hitYes') : p.canEmail === 'stopped' ? t('ev179m.share.hitStopped') : t('ev179m.share.hitNo'));

  const send = async () => {
    if (!picked) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await emailMonthReq(month, picked.tenantId, eventId, upcoming);
      onQueued(r.message);
    } catch (x) {
      // refused in the server's own words (no email on file, asked for no event emails, an empty month)
      setErr(errorText(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SheetContent
      title={t('ev179m.share.emailTitle', { what })}
      returnFocusTo={returnFocusTo}
      data-testid="month-email-sheet"
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={onClose} data-testid="month-email-cancel">
            {t('ev179m.share.cancel')}
          </Button>
          {picked && !can ? (
            <>
              {/* refused before the press: the button itself says why, and Print is offered in its place */}
              <Button disabled aria-describedby="month-email-no" data-testid="month-email-go">
                <Icon name="mail" /> {picked.canEmail === 'stopped' ? t('ev179m.share.stoppedBtn') : t('ev179m.share.noEmailBtn')}
              </Button>
              <Button onClick={() => onPrintInstead(picked.lang)} data-testid="month-email-print">
                <Icon name="printer" /> {t('ev179m.share.printInstead')}
              </Button>
            </>
          ) : (
            <Button disabled={busy || !picked} aria-busy={busy || undefined} onClick={() => send()} data-testid="month-email-go">
              <Icon name="mail" /> {busy ? t('ev179m.share.emailing') : picked ? t('ev179m.share.emailGo', { name: picked.name }) : t('ev179m.share.emailPick')}
            </Button>
          )}
        </>
      }
    >
      <div className="ev179m-stack">
        <p className="help">{t('ev179m.share.emailLead')}</p>
        <div className="field">
          <label htmlFor="month-email-q">{t('ev179m.share.find')}</label>
          {picked ? (
            <div className="chips dense">
              <span data-testid="month-email-picked">
                <b>{picked.name}</b> · {picked.unit}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setPicked(null);
                  setErr(null);
                }}
                data-testid="month-email-other"
              >
                {t('ev179m.share.pickOther')}
              </Button>
            </div>
          ) : (
            <>
              <input id="month-email-q" className="input" value={q} onChange={(x) => setQ(x.target.value)} maxLength={60} autoComplete="off" aria-describedby="month-email-hint month-email-res" data-testid="month-email-q" />
              <p className="help" id="month-email-hint">
                {t('ev179m.share.findHint')}
              </p>
              <div id="month-email-res" role="status">
                {q.trim().length >= 2 && people.isSuccess && !hits.length ? (
                  <p className="help" data-testid="month-email-none">
                    {t('ev179m.share.none')}
                  </p>
                ) : null}
                {hits.length ? (
                  <ul className="plain-list ev179m-hits" aria-label={t('ev179m.share.matches')}>
                    {hits.map((p) => (
                      <li key={p.tenantId}>
                        <Button variant="secondary" className="ev179m-hit" onClick={() => setPicked(p)} data-testid={`month-email-hit-${p.tenantId}`}>
                          <span>
                            {p.name} · {p.unit}
                          </span>
                          <span className="ev179m-hit-note">
                            <Icon name={p.canEmail === 'yes' ? 'mail' : 'alert'} aria-hidden="true" />
                            {hitWords(p)}
                          </span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </>
          )}
        </div>
        {picked && can ? (
          <p className="ev179m-line" data-testid="month-email-lang">
            <Icon name="info" aria-hidden="true" />
            <span>{t('ev179m.share.langOf', { lang: picked.lang === 'es' ? t('ev179m.share.es') : t('ev179m.share.en') })}</span>
          </p>
        ) : null}
        {picked && !can ? (
          <p className="ev179m-line ev179m-warn" id="month-email-no" role="status" data-testid="month-email-no">
            <Icon name="alert" aria-hidden="true" />
            <span>{picked.canEmail === 'stopped' ? t('ev179m.share.stopped', { name: picked.name }) : t('ev179m.share.noEmail', { name: picked.name })}</span>
          </p>
        ) : null}
        {err ? (
          <p className="field-err" role="alert" data-testid="month-email-err">
            {err}
          </p>
        ) : null}
      </div>
    </SheetContent>
  );
}

const sentDay = (iso: string, lang: string) => new Intl.DateTimeFormat(localeFor(lang), { month: 'short', day: 'numeric', timeZone: SERVICE_TZ }).format(new Date(iso));

/**
 * "Sent to 703 · Oct 9" — what was emailed to a person, kept by the server in the month's own file. On the Month
 * view: the whole-month sends (`month-sent`); on an event's page: that event's (`ev-share-sent`).
 */
export function MonthSentLines({ month, eventId = null, enabled }: { month: string; eventId?: string | null; enabled: boolean }) {
  const { t, i18n } = useTranslation();
  const q = useMonthSent(month, enabled);
  const rows: MonthSent[] = (q.data?.sent ?? []).filter((s) => (s.eventId ?? null) === eventId);
  if (!rows.length) return null;
  return (
    <ul className="plain-list ev179m-sent" aria-label={t('ev179m.share.sentLabel')} data-testid={eventId ? 'ev-share-sent' : 'month-sent'}>
      {rows.map((s) => (
        <li key={`${s.at}-${s.tenantId}`} className="ev179m-line" data-testid="month-sent-row">
          <Icon name={s.cancelled ? 'x' : 'mail'} aria-hidden="true" />
          <span>{t(s.cancelled ? 'ev179m.share.sentCancelled' : 'ev179m.share.sent', { unit: s.unit, day: sentDay(s.at, i18n.language) })}</span>
        </li>
      ))}
    </ul>
  );
}
